/* eslint-disable react-refresh/only-export-components -- WaymarkProvider re-exports its context hooks for legacy import paths, matching auth/useAuth.tsx */
import React from 'react';
import Waymark from '@waymark/waymark-sdk';
import { apiClient, ApiError } from '../../../lib/apiClient';
import { WaymarkContext } from './context';
import type { ProviderState, WaymarkContextValue, WaymarkInitial, WaymarkStage } from './context';

// Kept in sync with WAYMARK_PARTNER_UNAVAILABLE_CODE in selfservice-api
// (waymark-credentials.ts) — no shared package between the two.
const WAYMARK_PARTNER_UNAVAILABLE_CODE = 'waymark_partner_unavailable';
import * as analytics from '../../../lib/analytics';
import { EVENTS } from '../../../lib/analytics';
import type {
  IssueTokenResponse,
  WaymarkStatePayload,
  WaymarkStatus,
  WaymarkTransport,
} from './types';
import { normalizeUrl } from './normalizeUrl';
import { pickBroadcastRender } from './pickBroadcastRender';
import { claimEditor, editorOwnedByOther, isSessionVideo } from './editorSessions';

// Two-stage AI commercial generation, prefilled from the advertiser's website
// URL on step 1 and consumed on step 3. The provider lives ABOVE the wizard
// route so its SDK instance and hidden iframe persist across step navigations
// — the iframe is how the SDK communicates with Waymark, and remounting it
// per step would kill any in-flight topic_search / video_generation.
//
// State diagram (browser-side; webhooks can also promote on the server):
//
//   idle ──startGenerationForUrl──▶ topic_searching ──┬── COMPLETE ─▶ generating
//                                                     └── FAILED   ─▶ topic_failed
//                  generating ──┬── COMPLETE ─▶ ready
//                               └── FAILED   ─▶ generation_failed
//
// If the user edits their website URL and clicks Next again, we orphan the
// old topicId/requestId (no cancel API in the SDK) and start over.
//
// TODO(testing): provider-level state-machine coverage is still partial. The
// "re-evaluate after first prod incidents" trigger fired on 2026-08-26
// (CTV-801, the missed-`videoRendered` hang), and the finalize wait is now
// covered end to end in WaymarkProvider.test.tsx. Still uncovered and relying
// on manual testing: the three-branch resume effect's getVideoData probes and
// the auto-import remount-skip in UploadCreativeStep.

// These are passed verbatim to sdk.startVideoGeneration (verified live in the
// captured network requests + the SDK's Zod schema).
//
// RE-TESTED ON PROD 2026-08-26 (CTV-800), and prod honors both. Two
// commercials generated end to end through the staff Studio against the prod
// Waymark tenant, from different source sites, rendered IDENTICALLY at
// 30.037s and 1920x1080 (aspect 1.7778, exactly 16:9):
//   https://videos.waymark.com/web_video/renderedvideo/render/1111621.mp4
//   https://videos.waymark.com/web_video/renderedvideo/render/1111648.mp4
//
// The mismatches previously observed — 15s renders against `duration: 30` and
// a near-vertical aspect against `aspectRatio: '16:9'` — were all seen in the
// DEMO environment, and now look demo-only rather than a Waymark-wide bug.
// The support thread (requestId ef3db615-b628-4410-94ec-743dc8fb5785, sent
// 2026-05-19) can be updated with that. Keep watching prod renders: two
// samples are not a guarantee, and the SDK exposes no other knob to enforce
// these.
const FIXED_DURATION = 30; // CTV slot
const FIXED_ASPECT_RATIO = '16:9' as const;
const FIXED_LANGUAGE = 'en-US' as const;

// CTV-604 bridge-health tuning. The SDK's healthCheck() does a postMessage
// round-trip, so on a dead bridge (framing-refused iframe) it hangs forever —
// every probe must be raced against our own timeout, with timeout ⇒ unhealthy.
// The round-trip is in-process (child SPA answers locally, no network), so a
// healthy bridge answers in milliseconds and 2s is a generous ceiling.
const HEALTH_PROBE_TIMEOUT_MS = 2_000;
const OPEN_EDITOR_TIMEOUT_MS = 15_000;
const WATCHDOG_INTERVAL_MS = 5_000;
// A full-document (re)load inside the SDK iframe is the session-redirect
// signature (SPA navigation never fires the iframe's load event). Wait a
// beat before probing so the SDK's own reconnect can settle a legit reload.
const IFRAME_LOAD_PROBE_DELAY_MS = 1_000;
// Silent auto-recoveries allowed per editor-open session before we stop
// looping and show the manual "Reload editor" panel instead.
const MAX_AUTO_RECOVERIES = 2;

// CTV-801 finalize-poll tuning. `videoRendered` is push-only, fires once, and
// has no redelivery, so a tab that misses it waits forever (prod incident
// 2026-08-26: Finish at 14:19, the render sat finished on Waymark's side, and
// the wizard still read "Uploading" an hour later). While finalizing we also
// PULL: getVideoData is idempotent and always tells the truth. Broadcast
// renders normally land in 1-3 minutes, so poll tightly through that window
// and back off after it.
const FINALIZE_POLL_FAST_INTERVAL_MS = 10_000;
const FINALIZE_POLL_SLOW_INTERVAL_MS = 30_000;
const FINALIZE_POLL_FAST_WINDOW_MS = 3 * 60_000;
// Interval polling stops here (~42 probes worst case). The visibility probe
// stays armed past it, so a buyer returning to a long-abandoned tab still gets
// a fresh check. CTV-801 leaves the silent stop as-is; the buyer-facing
// "taking longer than expected" state is deliberately a separate follow-up.
const FINALIZE_POLL_MAX_MS = 15 * 60_000;
// Each probe is a network call, and the next tick is only scheduled once the
// previous one settles. A hung request would therefore stall the backstop
// permanently, so every probe is raced (timeout ⇒ "no render yet, try again").
const FINALIZE_PROBE_TIMEOUT_MS = 15_000;

// CTV-801: the shape getVideoData resolves to, named so the finalize poll can
// race it against a timeout without restating the SDK's return type.
type VideoDataLike = Awaited<ReturnType<Waymark['getVideoData']>>;

function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([
    p.catch(() => fallback),
    new Promise<T>((resolve) => {
      setTimeout(() => resolve(fallback), ms);
    }),
  ]);
}

function probeHealth(sdk: Waymark, ms = HEALTH_PROBE_TIMEOUT_MS): Promise<boolean> {
  try {
    return withTimeout(sdk.frameCommunicator.healthCheck(), ms, false);
  } catch {
    return Promise.resolve(false);
  }
}

// CTV-800 cross-write fix (prod, 2026-08-27: a Studio row adopted another
// staff member's render). Waymark's embed session is a `Partitioned` cookie
// shared by EVERY Waymark iframe on our origin, and sdk.cleanup() only logs
// the account out while its iframe is still attached: it awaits the logout
// postMessage BEFORE removing the node, and only detaches its `document`
// listeners after that. Yanking the node first (the CTV-604 dead-bridge
// workaround) therefore skipped the logout, so the NEXT iframe booted already
// logged in as the previous commercial's account and emitted that account's
// events, which the provider then adopted.
//
// So: let the SDK run its full teardown with the iframe attached (logout,
// close, node removal, listener removal), bounded by a timeout so a dead
// bridge cannot hang us; only if that does not settle do we yank the node
// and detach the listeners ourselves. Callers must await the returned promise
// (via `teardownPromiseRef`) before building the next instance, because a
// late logout would clear the session the new instance just logged into.
const SDK_DISPOSE_TIMEOUT_MS = 2_500;

// A Finish (`videoCompleted`) is only ours if it came out of OUR editor. The
// SDK fires `editorExited` for Finish too, in either order relative to
// `videoCompleted`, so "our editor was open within this window" is the test
// (the review session uses the same idea, see useWaymarkEditorSession.ts).
const OWN_FINISH_GRACE_MS = 5_000;

// CTV-800 resume probe for a website read (topic search). The SDK exposes no
// "get topic" call: a subscription tells Waymark's page to poll and emit
// updates, and whether a read that finished while nobody was subscribed is
// announced again on re-subscribe is not documented. The one probe available
// is the handoff itself: startVideoGeneration succeeds only for a complete
// topic. So a resumed read tries the handoff after a delay a live
// subscription would normally have beaten, then keeps trying on an interval
// while the row still says it is reading, up to a cap.
const TOPIC_PROBE_DELAY_MS = 15_000;
const TOPIC_PROBE_INTERVAL_MS = 30_000;
const TOPIC_PROBE_MAX_MS = 10 * 60_000;

// Mirrors the tail of the SDK's cleanup(): the `listeners` map is private in
// the typings but present at runtime, and it is the only handle we have once
// cleanup() itself has rejected on a dead bridge.
function detachSdkListeners(sdk: Waymark): void {
  const listeners = (sdk as unknown as { listeners?: Record<string, Map<unknown, EventListener>> })
    .listeners;
  if (!listeners) return;
  for (const [eventName, callbacks] of Object.entries(listeners)) {
    if (!callbacks) continue;
    for (const listener of callbacks.values()) {
      document.removeEventListener(`waymark:${eventName}`, listener);
    }
    callbacks.clear();
  }
}

/**
 * `logout-first`: a different account is about to log in (commercial
 * switch), so the SDK's attached teardown runs first and its logout clears
 * the shared session; only a bridge that does not answer gets yanked.
 * `yank-first`: the SAME account is being rebuilt (CTV-604 dead-bridge
 * recovery), so no logout is needed and the node comes out immediately, as
 * before, so recovery is not held for a round trip the bridge cannot make.
 */
async function disposeSDK(sdk: Waymark, mode: 'logout-first' | 'yank-first'): Promise<void> {
  const yank = () => (sdk as unknown as { iframe?: HTMLIFrameElement }).iframe?.remove();
  if (mode === 'yank-first') yank();
  const settled = await withTimeout(
    sdk.cleanup().then(() => true),
    SDK_DISPOSE_TIMEOUT_MS,
    false,
  );
  if (settled) return;
  // Dead bridge (or Waymark not answering): removing the node makes penpal
  // reject the hung calls so cleanup() settles, then finish what it never
  // reached. Only THIS instance's iframe, never the whole container: a newer
  // instance may already live there.
  yank();
  detachSdkListeners(sdk);
}

type EditorPhase = 'closed' | 'connecting' | 'open' | 'failed';
type OpenOutcome = 'opened' | 'failed' | 'stale';

// IDs of the in-flight generation run, mirrored into a ref so reinitSDK can
// re-attach subscriptions after replacing a dead iframe without threading
// them through every caller.
interface ActiveRun {
  topicId: string | null;
  requestId: string | null;
  videoId: string | null;
  sourceUrl: string;
}

interface PersistInput extends Partial<ProviderState> {
  status: WaymarkStatus;
  topicId?: string | null;
  generationRequestId?: string | null;
  errorCode?: string | null;
  // CTV-775: the client is deliberately discarding the prior run (Start over
  // or a source-URL change); lets the server reset a ready+rendered row.
  restart?: boolean;
}

// CTV-653: the context types + hooks moved to ./context (SDK-free) so the
// panel/disclosure consumers don't drag the SDK into their import graph.
// Re-exported here so pre-split import sites keep compiling.
export { useWaymark, useWaymarkOptional } from './context';
export type { WaymarkInitial } from './context';

interface WaymarkProviderProps {
  slug: string;
  // CTV-775: the AI commercial this provider is mounted on. `commercialToken`
  // is the server-minted access token the /waymark/token and /waymark/state
  // endpoints accept; `commercialId` is the row id (= Waymark externalID).
  // ONE provider stays mounted per surface for its whole life; when the
  // active commercial changes it tears the SDK down and rehydrates from the
  // new `initial` in place (a fresh Waymark account login, but no React
  // remount, so the surface's staged state below it survives). With
  // `commercialId` null (no active commercial) the context value is null.
  commercialToken: string | null;
  commercialId: string | null;
  // CTV-775 analytics context: which surface mounted this provider and the
  // 1-based position of the commercial on it. Stamped onto every waymark_*
  // event for the life of the mount.
  surface: analytics.WaymarkSurface;
  commercialOrdinal: number;
  /** Initial state hydrated from the commercial row (server-side). Null while loading. */
  initial: WaymarkInitial | null;
  // CTV-785: every persisted state write is reported here too, so the
  // surface's commercial list (useAiCommercials.recordState) never goes
  // stale behind this provider.
  onStateChange?: (commercialId: string, patch: Partial<WaymarkInitial>) => void;
  // CTV-800: override how this provider reaches the server. The buyer
  // surfaces omit it and get the public commercial-token endpoints; the staff
  // Studio passes its authenticated super_admin ones, keyed on the row id.
  // Parameterizing the two call sites keeps ONE provider rather than a fork.
  transport?: WaymarkTransport;
  children: React.ReactNode;
}

export const WaymarkProvider: React.FC<WaymarkProviderProps> = ({
  slug,
  commercialToken,
  commercialId,
  surface,
  commercialOrdinal,
  initial,
  onStateChange,
  transport,
  children,
}) => {
  // CTV-775: register the analytics context for the active commercial; clear
  // when none is active or on unmount so nothing stale is emitted.
  React.useEffect(() => {
    if (commercialId == null) {
      analytics.registerWaymarkContext(null);
      return undefined;
    }
    analytics.registerWaymarkContext({ surface, commercialOrdinal });
    return () => analytics.registerWaymarkContext(null);
  }, [surface, commercialOrdinal, commercialId]);

  const [state, setState] = React.useState<ProviderState>({
    status: initial?.status ?? 'idle',
    sourceUrl: initial?.sourceUrl ?? null,
    videoId: initial?.videoId ?? null,
    renderedUrl: initial?.renderedUrl ?? null,
    errorMessage: initial?.errorMessage ?? null,
    partnerUnavailable: false,
  });
  // CTV-604: editor overlay phase machine. `isEditorOpen` (the public
  // context field) derives from it, so consumers are unchanged. connecting /
  // failed both render the opaque EditorOverlayPanel above the iframe — the
  // user never sees a browser-native error page. Dead-bridge detection
  // auto-recovers silently (phase → connecting); `failed` only shows after
  // the auto-recovery budget is spent.
  const [editorPhase, setEditorPhase] = React.useState<EditorPhase>('closed');
  const isEditorOpen = editorPhase !== 'closed';
  const editorPhaseRef = React.useRef(editorPhase);
  editorPhaseRef.current = editorPhase;
  const [isFinalizing, setIsFinalizing] = React.useState(false);
  const [isHydrating, setIsHydrating] = React.useState(initial == null);
  // CTV-775 ledger: stage entries observed in this session (see context.ts).
  // The first stage seen after hydration is a BASELINE, not an entry, so a
  // mid-flow refresh never invents a time for work done in a prior session.
  const [stageEnteredAt, setStageEnteredAt] = React.useState<
    Partial<Record<WaymarkStage, number>>
  >({});
  const prevLedgerStageRef = React.useRef<WaymarkStage | null | undefined>(undefined);
  // One-shot hydration. The parent may rebuild the fallback `initial` object
  // on every render (for fresh commercials with no persisted state), and the
  // `[initial]` dep below sees a new reference each time. Without this guard, hydration would re-fire on every parent render
  // and reset our in-flight state (status: 'topic_searching' → 'idle'),
  // which then breaks the startGenerationForUrl no-op check and causes a
  // fresh `createTopic` call (whose status polling races and 404s).
  const hasHydratedRef = React.useRef(false);

  const sdkRef = React.useRef<Waymark | null>(null);
  // Promise-based dedupe ref so two concurrent ensureSDK() callers (e.g. under
  // React Strict Mode where effects fire twice in dev) share the same in-flight
  // init rather than each creating a new SDK + calling createAccount twice
  // (the second call would race and either trigger AccountAlreadyExistsError
  // or leave Apollo's internal store in a confused state mid-init).
  const sdkInitPromiseRef = React.useRef<Promise<Waymark | null> | null>(null);
  // CTV-800 cross-write fix: the previous instance's teardown (see
  // disposeSDK). ensureSDK awaits it before constructing the next instance so
  // the old account is logged out of the shared Waymark session first.
  const teardownPromiseRef = React.useRef<Promise<void>>(Promise.resolve());
  // When OUR editor last closed (epoch ms); see OWN_FINISH_GRACE_MS.
  const editorExitedAtRef = React.useRef(0);
  // CTV-800: the run nonce whose read-to-generation handoff has begun, so the
  // subscription's COMPLETE and the resume probe never both start a video.
  const topicHandoffRef = React.useRef<number | null>(null);
  const topicProbeTimerRef = React.useRef<number | null>(null);
  // The last element focused OUTSIDE the SDK container, to hand focus back to
  // when the hidden iframe grabs it (see the focus guard effect below).
  const lastFocusedRef = React.useRef<HTMLElement | null>(null);
  // The hidden iframe container. Mounted once and lives for the provider's
  // lifetime. We toggle its `display` when opening/closing the editor.
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  // Tracks the URL we're currently generating for. Used to no-op repeat
  // startGenerationForUrl calls and to detect URL changes.
  const inflightUrlRef = React.useRef<string | null>(initial?.sourceUrl ?? null);
  // Each generation run gets a fresh nonce. Subscriptions check the nonce
  // before applying status updates so an orphaned old run can't promote the
  // wizard back into `generating` after the user has restarted.
  const runNonceRef = React.useRef(0);
  // One-shot resume guard (see the resume effect below).
  const hasResumedRef = React.useRef(false);
  // CTV-604: current run IDs for post-reinit subscription re-attach.
  const activeRunRef = React.useRef<ActiveRun | null>(null);
  // Stale-open guard: bumped by every openEditor/recoverEditor/closeEditor so
  // an in-flight open sequence can't flip the phase after the user moved on.
  const openNonceRef = React.useRef(0);
  // CTV-785: this provider's identity for the editor arbiter (see
  // editorSessions.ts). A review session on a committed commercial runs a
  // second SDK instance on the same page, and the SDK broadcasts its events
  // on `document`, so the handlers below check the claim and the video id.
  const ownerIdRef = React.useRef(`provider:${Math.random().toString(36).slice(2)}`);
  // CTV-785: what the last Finish started: the file held before it and when,
  // so videoRendered can tell the new render from a stale entry.
  // CTV-800: `videoId` rides along because React state (stateRef) only
  // catches up on the next render; a render event landing in the same batch
  // as the Finish must still match the video the Finish named.
  const finalizingRef = React.useRef<{
    previousUrl: string | null;
    since: number;
    videoId: string;
  } | null>(null);
  // CTV-775: which commercial this provider currently serves. SDK event
  // handlers compare against it so a late event from a torn-down commercial
  // can never write into the one that replaced it.
  const commercialIdRef = React.useRef(commercialId);
  commercialIdRef.current = commercialId;
  const onStateChangeRef = React.useRef(onStateChange);
  onStateChangeRef.current = onStateChange;

  // CTV-775: switch commercials in place. Runs BEFORE the hydration and
  // resume effects below (declaration order), so those see a clean slate and
  // rehydrate / resume for the new commercial. The old instance is disposed
  // of WITH its logout (see disposeSDK), and ensureSDK waits for that.
  const servedCommercialIdRef = React.useRef(commercialId);
  React.useEffect(() => {
    if (servedCommercialIdRef.current === commercialId) return;
    servedCommercialIdRef.current = commercialId;
    const old = sdkRef.current;
    sdkRef.current = null;
    sdkInitPromiseRef.current = null;
    // CTV-800 cross-write fix: log the old account out BEFORE the next
    // instance can log in (ensureSDK awaits this). An init still in flight
    // disposes of itself when it finds the commercial changed under it.
    teardownPromiseRef.current = old ? disposeSDK(old, 'logout-first') : Promise.resolve();
    if (topicProbeTimerRef.current != null) window.clearTimeout(topicProbeTimerRef.current);
    topicProbeTimerRef.current = null;
    topicHandoffRef.current = null;
    runNonceRef.current += 1;
    openNonceRef.current += 1;
    activeRunRef.current = null;
    hasResumedRef.current = false;
    hasHydratedRef.current = false;
    inflightUrlRef.current = initial?.sourceUrl ?? null;
    setEditorPhase('closed');
    setIsFinalizing(false);
    // The next commercial gets a fresh ledger: no stamps, and its first
    // observed stage becomes the new baseline.
    setStageEnteredAt({});
    prevLedgerStageRef.current = undefined;
    setState({
      status: initial?.status ?? 'idle',
      sourceUrl: initial?.sourceUrl ?? null,
      videoId: initial?.videoId ?? null,
      renderedUrl: initial?.renderedUrl ?? null,
      errorMessage: initial?.errorMessage ?? null,
      partnerUnavailable: false,
    });
    setIsHydrating(initial == null);
    if (initial != null) hasHydratedRef.current = true;
    // `initial` is deliberately read, not depended on: the switch is keyed on
    // the commercial id alone; the hydration effect owns later `initial` moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commercialId]);

  // Hydrate when `initial` arrives (the parent waits for the draft load).
  React.useEffect(() => {
    if (hasHydratedRef.current) return;
    if (initial == null) return;
    // CTV-775 ledger: the hydration jump is not an observed transition. The
    // baseline resets so the ledger's first post-hydration stage gets no
    // invented entry stamp.
    prevLedgerStageRef.current = undefined;
    setState({
      status: initial.status,
      sourceUrl: initial.sourceUrl,
      videoId: initial.videoId,
      renderedUrl: initial.renderedUrl,
      errorMessage: initial.errorMessage,
      // Re-derived at token time, not persisted — a fresh hydrate starts clean.
      partnerUnavailable: false,
    });
    // Also sync inflightUrlRef so the no-op check in startGenerationForUrl
    // knows we already have a run going for this URL. Without this, a
    // refresh on /start/video with a persisted `ready` state would re-fire
    // startGenerationForUrl (inflightUrlRef.current is still null from the
    // pre-hydration initializer) and spawn a new topic/video.
    inflightUrlRef.current = initial.sourceUrl;
    setIsHydrating(false);
    hasHydratedRef.current = true;
  }, [initial]);

  // CTV-775 ledger: stamp stage entries as they happen. Only transitions we
  // actually witness count; the first stage seen after hydration is the
  // baseline (prevLedgerStageRef === undefined) and gets no stamp.
  const ledgerStage: WaymarkStage | null = isFinalizing
    ? 'finalizing'
    : state.status === 'topic_searching'
      ? 'analyzing'
      : state.status === 'generating'
        ? 'drafting'
        : state.status === 'ready'
          ? state.renderedUrl
            ? 'ready'
            : 'customize'
          : null;
  React.useEffect(() => {
    const prev = prevLedgerStageRef.current;
    prevLedgerStageRef.current = ledgerStage;
    if (prev === undefined || prev === ledgerStage || ledgerStage == null) return;
    setStageEnteredAt((m) => (m[ledgerStage] != null ? m : { ...m, [ledgerStage]: Date.now() }));
  }, [ledgerStage]);

  // CTV-468: wall-clock of the last generation kickoff, so we can report the
  // generation-start → editor-open gap as a proxy for how long renders take.
  const generationStartedAtRef = React.useRef<number | null>(null);
  // CTV-800: the two server calls this provider makes, in one place. Default
  // is the buyer path: the public endpoints keyed on the commercial's access
  // token, null (= no-op) until a token exists, exactly as before. The staff
  // Studio passes its own, so neither call site below names an endpoint.
  const defaultTransport = React.useMemo<WaymarkTransport | null>(() => {
    if (!commercialToken) return null;
    return {
      issueToken: ({ forceCreate }) =>
        apiClient.post<IssueTokenResponse>('/waymark/token', {
          commercialToken,
          slug,
          ...(forceCreate ? { forceCreate: true } : {}),
        }),
      persistState: async (body) => {
        await apiClient.post('/waymark/state', { commercialToken, slug, ...body });
      },
    };
  }, [commercialToken, slug]);
  const transportRef = React.useRef<WaymarkTransport | null>(null);
  transportRef.current = transport ?? defaultTransport;
  // CTV-800: "can this provider reach the server for this commercial", which
  // is what the resume effect below actually needs. It used to ask
  // `commercialToken` instead, which is the BUYER's way of reaching the
  // server; the staff Studio reaches it through `transport` and never holds a
  // token, so the whole resume path silently did nothing there.
  const canReachServer = (transport ?? defaultTransport) != null;
  // Live mirror of `state` so recovery paths (watchdog, recoverEditor) read
  // current videoId/status instead of a stale closure.
  const stateRef = React.useRef(state);
  stateRef.current = state;
  // Prevents overlapping watchdog probes (each probe can take up to 2s).
  const isProbingRef = React.useRef(false);
  // CTV-801: same idea for the finalize poll, kept separate from the health
  // probe above. That one is a local postMessage round-trip; this one is a
  // network call to Waymark and can outlive its own tick.
  const isFinalizePollingRef = React.useRef(false);
  // Silent auto-recoveries used in the current editor-open session. Reset on
  // every manual open / panel-driven reload so each session gets a fresh
  // budget; without the cap a Waymark-side outage would loop reinits forever.
  const autoRecoverCountRef = React.useRef(0);
  // Ref indirection so ensureSDK (defined before recoverEditor) can route the
  // iframe-load probe into the shared dead-bridge handler.
  const handleBridgeDownRef = React.useRef<(trigger: string) => void>(() => {});

  const persistState = React.useCallback(async (input: PersistInput) => {
    const wire = transportRef.current;
    if (!wire) return;
    // CTV-785: mirror the write into the surface's snapshot first; the list
    // above us must agree with what this provider believes.
    const forCommercial = commercialIdRef.current;
    if (forCommercial && onStateChangeRef.current) {
      const patch: Partial<WaymarkInitial> = { status: input.status };
      if (input.sourceUrl !== undefined) patch.sourceUrl = input.sourceUrl;
      if (input.topicId !== undefined) patch.topicId = input.topicId;
      if (input.generationRequestId !== undefined) patch.generationRequestId = input.generationRequestId;
      if (input.videoId !== undefined) patch.videoId = input.videoId;
      if (input.renderedUrl !== undefined) patch.renderedUrl = input.renderedUrl;
      if (input.errorMessage !== undefined) patch.errorMessage = input.errorMessage;
      onStateChangeRef.current(forCommercial, patch);
    }
    // CTV-800 cross-write fix: only the fields the caller touched go on the
    // wire. The server treats an ABSENT field as "leave the column alone" and
    // `null` as a reset, and the old `?? null` fill-in turned every partial
    // write (adopting a render, say) into a wipe of the row's topic and
    // generation ids, which is what left the cross-assigned row unrecoverable.
    const payload: WaymarkStatePayload = { status: input.status };
    if (input.sourceUrl !== undefined) payload.sourceUrl = input.sourceUrl;
    if (input.topicId !== undefined) payload.topicId = input.topicId;
    if (input.generationRequestId !== undefined) {
      payload.generationRequestId = input.generationRequestId;
    }
    if (input.videoId !== undefined) payload.videoId = input.videoId;
    if (input.renderedUrl !== undefined) payload.renderedUrl = input.renderedUrl;
    if (input.errorCode !== undefined) payload.errorCode = input.errorCode;
    if (input.errorMessage !== undefined) payload.errorMessage = input.errorMessage;
    if (input.restart) payload.restart = true;
    try {
      await wire.persistState(payload);
    } catch (e) {

      console.warn('[waymark] persistState failed', e);
    }
  }, []);

  /**
   * CTV-801: the single way a finished broadcast render is adopted. Called by
   * the `videoRendered` event (push) and by the finalize poll (pull) so both
   * routes land identical state and neither can drift from the other.
   * `source` is analytics-only: it measures how often the push path is the one
   * that actually delivers, which is the evidence for whether the server-side
   * `video.rendered` webhook is worth wiring up.
   */
  const adoptRender = React.useCallback(
    (videoId: string, url: string, source: 'event' | 'poll') => {
      finalizingRef.current = null;
      analytics.track(EVENTS.WAYMARK_VIDEO_RENDERED, { source });
      setIsFinalizing(false);
      const next: ProviderState = {
        status: 'ready',
        partnerUnavailable: false,
        sourceUrl: inflightUrlRef.current,
        videoId,
        renderedUrl: url,
        errorMessage: null,
      };
      setState(next);
      void persistState({
        status: next.status,
        sourceUrl: next.sourceUrl,
        videoId: next.videoId,
        renderedUrl: next.renderedUrl,
        // A landed render supersedes any earlier failure message; the topic
        // and generation ids are deliberately NOT sent, so they survive.
        errorCode: null,
        errorMessage: null,
      });
    },
    [persistState],
  );

  /**
   * Lazily instantiate the SDK + authenticate. Returns the SDK ready to
   * receive method calls. Safe to call multiple times — subsequent calls
   * return the cached instance.
   */
  const ensureSDK = React.useCallback(async (): Promise<Waymark | null> => {
    if (sdkRef.current) return sdkRef.current;
    if (sdkInitPromiseRef.current) return sdkInitPromiseRef.current;
    // CTV-800: readiness is "we have a way to reach the server", which for
    // the buyer surfaces is still "we have a commercial token" (the default
    // transport is null without one) and for the Studio is its own transport.
    if (!transportRef.current) return null;
    if (!containerRef.current) return null;

    // CTV-775: the commercial this SDK instance belongs to. Every event handler
    // below checks it so a late event from a torn-down commercial is dropped.
    const forCommercial = commercialIdRef.current;
    const stillCurrent = () => commercialIdRef.current === forCommercial;

    const initPromise = (async (): Promise<Waymark | null> => {
      // CTV-800 cross-write fix: the previous instance must be logged out of
      // the shared Waymark session before this one logs in (see disposeSDK).
      await teardownPromiseRef.current;
      if (!stillCurrent()) return null;
      // requestAuth mints a fresh JWT. forceCreate is the recovery flag
      // for "Waymark says no account exists" — our DB might think one was
      // created while the upstream call actually failed.
      const requestAuth = async (forceCreate: boolean): Promise<IssueTokenResponse | null> => {
        const wire = transportRef.current;
        if (!wire) return null;
        try {
          return await wire.issueToken({ forceCreate });
        } catch (e) {
          // CTV-643: the backend can't sign for this commercial's pinned
          // partner: the publisher's Waymark agreement was cleared/repointed
          // after the account was created. Not retryable against Waymark, so flag it so
          // the panel shows a dead-end state instead of spinning forever.
          if (
            e instanceof ApiError &&
            e.status === 409 &&
            (e.body as { code?: string } | undefined)?.code === WAYMARK_PARTNER_UNAVAILABLE_CODE
          ) {
            setState((s) => ({ ...s, partnerUnavailable: true }));
          }
          console.warn('[waymark] token issue failed', e);
          return null;
        }
      };

      const auth = await requestAuth(false);
      if (!auth) return null;

      // CTV-554: the container ref can become null between the entry check
      // above and this point if the wizard body remounts during the await
      // (e.g. seat switch via "Advertise with another business"). The SDK
      // constructor throws InvalidParameterError on a null domElement, so
      // bail cleanly — the next identity's WaymarkProvider mount will
      // re-issue the token and re-init against the fresh container.
      if (!containerRef.current) {
        console.info('[waymark] container unmounted during init — aborting');
        return null;
      }

      // Constructor with `domElement` already appends the iframe and starts
      // loading the SDK page — no separate `mount()` call is needed (verified
      // in @waymark/waymark-sdk@4.1.0 source: index.mjs line 638). Calling
      // mount() with the same element is a no-op, but explicit clarity here.
      const sdk = new Waymark(auth.partnerID, {
        authMode: 'jwt',
        environment: auth.environment,
        domElement: containerRef.current,
      });

      const authenticate = async (a: IssueTokenResponse): Promise<void> => {
        if (a.mode === 'create') {
          await sdk.createAccount(a.token);
        } else {
          await sdk.loginAccount(a.token);
        }
      };

      try {
        await authenticate(auth);
      } catch (e) {
        // If loginAccount failed because Waymark has no record of the
        // account (our DB marked account_created_at but the upstream
        // createAccount actually failed earlier — most often during the
        // dotenv-$ secret-mangling era), recover by forcing a fresh
        // createAccount round-trip.
        const msg = e instanceof Error ? e.message : '';
        // Demo says "No account found"; prod wraps the same condition in a
        // generic "There was a problem authenticating the user" (seen live in
        // CTV-775 when a demo-era account_created_at met the prod tenant).
        // A wrongly-triggered forceCreate is safe: createAccount on an
        // existing externalID fails and we give up cleanly below.
        const looksLikeNoAccount =
          auth.mode === 'login' &&
          /No account found|problem authenticating the user/i.test(msg);
        if (!looksLikeNoAccount) {
          console.warn('[waymark] auth failed', e);
          try { await sdk.cleanup(); } catch { /* noop */ }
          return null;
        }
        console.info('[waymark] login failed with "No account found" — retrying as create');
        const retry = await requestAuth(true);
        if (!retry || retry.mode !== 'create') {
          console.warn('[waymark] forceCreate retry did not return a create token');
          try { await sdk.cleanup(); } catch { /* noop */ }
          return null;
        }
        try {
          await authenticate(retry);
        } catch (e2) {

          console.warn('[waymark] auth failed (forceCreate retry)', e2);
          try { await sdk.cleanup(); } catch { /* noop */ }
          return null;
        }
      }

      // CTV-800 cross-write fix: the commercial changed while we were logging
      // in. This instance now holds the OLD account on the shared session, so
      // dispose of it (logout included) rather than hand it back.
      if (!stillCurrent()) {
        teardownPromiseRef.current = disposeSDK(sdk, 'logout-first');
        return null;
      }

      return sdk;
    })();
    sdkInitPromiseRef.current = initPromise;

    const sdk = await initPromise;
    if (!sdk) {
      sdkInitPromiseRef.current = null;
      return null;
    }

    sdk.on('videoCompleted', (videoData) => {
      if (!stillCurrent()) return;
      // CTV-785: a review session's Finish on the same page is not ours.
      if (editorOwnedByOther(ownerIdRef.current) || isSessionVideo(videoData.id)) return;
      // CTV-800 cross-write fix: the SDK broadcasts every iframe's events on
      // `document`, and an iframe that inherited another account's session
      // can report THAT account's Finish. A Finish is ours only if it came
      // out of our own editor, for the video we know (if we know one yet).
      const ownEditor =
        editorPhaseRef.current === 'open' ||
        Date.now() - editorExitedAtRef.current < OWN_FINISH_GRACE_MS;
      if (!ownEditor) {
        console.info('[waymark] ignoring videoCompleted: not from our editor', { id: videoData.id });
        return;
      }
      const knownAtFinish = stateRef.current.videoId;
      if (knownAtFinish && videoData.id !== knownAtFinish) {
        console.info('[waymark] ignoring videoCompleted for another video', { id: videoData.id });
        return;
      }
      finalizingRef.current = {
        previousUrl: stateRef.current.renderedUrl,
        since: Date.now(),
        videoId: videoData.id,
      };
      // Fires when the user clicks Finish inside the editor — broadcast-quality
      // rendering ALWAYS begins regardless of whether the video had a previous
      // successful render. `videoData.renders` may still include stale entries
      // from earlier Finish clicks, so we don't use them to decide whether to
      // show the spinner. Instead: always flip `isFinalizing` true, and clear
      // any prior `renderedUrl` (it's stale until the new render lands via the
      // `videoRendered` event). State stays at `ready` — AI generation isn't
      // being redone, just the final render.
      analytics.track(EVENTS.WAYMARK_VIDEO_COMPLETED);
      setIsFinalizing(true);
      setState((s) => ({
        ...s,
        status: 'ready',
        videoId: videoData.id,
        renderedUrl: null,
        errorMessage: null,
      }));
      void persistState({
        status: 'ready',
        sourceUrl: inflightUrlRef.current,
        videoId: videoData.id,
        renderedUrl: null,
      });
    });

    sdk.on('videoRendered', (videoData) => {
      if (!stillCurrent()) return;
      // CTV-785: renders of a review session's video, or of any video other
      // than the one we know, are not ours.
      if (isSessionVideo(videoData.id)) return;
      // CTV-800 cross-write fix: adopt a render ONLY for the video we already
      // know. The old "no known video yet, so any render is ours" window is
      // exactly where a Studio row mid topic-search took another staff
      // member's render (prod, 2026-08-27). A render can never precede our
      // own knowledge of the video: generation and Finish both record the id
      // first.
      // stateRef lags an event by a render, so also read the refs that are
      // written synchronously: the Finish (finalizingRef) and the generation
      // run (activeRunRef).
      const knownVideoId =
        stateRef.current.videoId ??
        finalizingRef.current?.videoId ??
        activeRunRef.current?.videoId ??
        null;
      if (!knownVideoId || videoData.id !== knownVideoId) {
        console.info('[waymark] ignoring videoRendered for a video that is not ours', {
          id: videoData.id,
          known: knownVideoId,
        });
        return;
      }
      // `videoRendered` can fire more than once — Waymark renders both
      // `email` and `broadcast_quality` formats, and we've seen the event
      // emit for the email format before broadcast_quality is ready. If we
      // clear `isFinalizing` on that first emission, the UI briefly drops
      // back to the "click Review" placeholder before the real render
      // lands. CTV-785: the payload also lists every earlier render, so the
      // picker takes the newest one and never the file we already held.
      const rendered = pickBroadcastRender(videoData.renders ?? [], finalizingRef.current ?? {});
      if (!rendered?.url) return;
      adoptRender(videoData.id, rendered.url, 'event');
    });

    // editorExited fires for BOTH "Exit" and "Finish" inside the editor.
    // Either way, the editor view inside Waymark's iframe has closed and
    // its content goes blank — so we need to shrink our fullscreen overlay
    // back to its hidden 1×1 size or the user is left staring at white.
    sdk.on('editorExited', () => {
      if (!stillCurrent()) return;
      // CTV-785: a review session's editor closing is not ours.
      if (editorOwnedByOther(ownerIdRef.current)) return;
      // Only an exit of OUR editor opens the Finish grace window.
      if (editorPhaseRef.current === 'open') editorExitedAtRef.current = Date.now();
      analytics.track(EVENTS.WAYMARK_EDITOR_EXITED);
      openNonceRef.current += 1;
      setEditorPhase('closed');
    });

    // CTV-604: analytics-only taps. waymarkOpenFailed / editorOpenFailed
    // already reject the corresponding open promise (recovery handles that
    // path); wiring them into recovery here would double-fire it.
    const trackSdkError = (event: string) => (e: unknown) => {
      analytics.track(EVENTS.WAYMARK_SDK_ERROR, {
        event,
        message: e instanceof Error ? e.message : String(e),
      });
    };
    sdk.on('error', trackSdkError('error'));
    sdk.on('waymarkOpenFailed', trackSdkError('waymarkOpenFailed'));
    sdk.on('editorOpenFailed', trackSdkError('editorOpenFailed'));

    // CTV-604 fast detection: the SDK never changes iframe.src and its SPA
    // navigates without full document loads — so any `load` event after the
    // initial one is the session-redirect signature (Waymark bounced the
    // iframe to a re-auth URL, likely framing-refused). Probe shortly after
    // (the delay lets the SDK's own reconnect settle a legitimate reload).
    const iframe = containerRef.current?.querySelector('iframe');
    if (iframe) {
      iframe.addEventListener('load', () => {
        setTimeout(() => {
          const current = sdkRef.current;
          if (current !== sdk) return; // torn down / replaced meanwhile
          void probeHealth(current).then((ok) => {
            if (!ok) handleBridgeDownRef.current('iframe_load');
          });
        }, IFRAME_LOAD_PROBE_DELAY_MS);
      });
    }

    sdkRef.current = sdk;
    return sdk;
  }, [persistState, adoptRender]);

  // ---- Subscription helpers ----
  // Extracted so both initial start and resume-after-hydration can reuse the
  // same status-handling logic. Both helpers re-establish the SDK subscription
  // for an existing topicId / requestId — they do NOT issue create calls.

  const attachGenerationStatusSub = React.useCallback(async (
    sdk: Waymark,
    args: { requestId: string; videoId: string; sourceUrl: string; topicId: string; nonce: number },
  ) => {
    const { requestId, videoId, sourceUrl, topicId, nonce } = args;
    activeRunRef.current = { topicId, requestId, videoId, sourceUrl };
    await sdk.subscribeToGenerationStatusUpdates(requestId, (genUpdate) => {
      if (runNonceRef.current !== nonce) return;
      if (genUpdate.status === 'FAILED') {
        const msg = genUpdate.error?.message ?? 'Video generation failed';
        analytics.track(EVENTS.WAYMARK_GENERATION_FAILED, { code: genUpdate.error?.code ?? null });
        setState((s) => ({ ...s, status: 'generation_failed', errorMessage: msg }));
        void persistState({
          status: 'generation_failed',
          sourceUrl,
          topicId,
          generationRequestId: requestId,
          videoId,
          errorCode: genUpdate.error?.code ?? null,
          errorMessage: msg,
        });
        return;
      }
      if (genUpdate.status === 'COMPLETE') {
        // AI generation finished — the video exists as a DRAFT on Waymark's
        // side. We DON'T get a programmatic preview URL: the SDK's Zod schema
        // strips `previewUrl` from startVideoGeneration's response, and
        // `getVideoData` returns `renders: []` for un-Finished videos
        // (verified 2026-05-18). The actual preview lives inside Waymark's
        // editor iframe, which the user opens via Customize. The
        // broadcast-quality `renderedUrl` only populates after they click
        // Finish in the editor → `videoRendered` event fires.
        analytics.track(EVENTS.WAYMARK_READY);
        setState((s) => ({ ...s, status: 'ready' }));
        void persistState({
          status: 'ready',
          sourceUrl,
          topicId,
          generationRequestId: requestId,
          videoId,
        });
      }
    });
  }, [persistState]);

  /**
   * The handoff from a finished website read to video generation: the ONE
   * step of a run that Waymark does not take on its own (the browser asks
   * for the video). Called by the topic subscription's COMPLETE update and by
   * the resume probe; `topicHandoffRef` makes sure only one of them does it
   * for a given run. Throws when Waymark refuses (the callers decide what
   * that means: the subscription reports a failure, the probe keeps waiting).
   */
  const beginVideoGeneration = React.useCallback(async (
    sdk: Waymark,
    args: { topicId: string; sourceUrl: string; nonce: number; source: 'event' | 'probe' },
  ): Promise<void> => {
    const { topicId, sourceUrl, nonce, source } = args;
    const gen = await sdk.startVideoGeneration({
      topicId,
      aspectRatio: FIXED_ASPECT_RATIO,
      duration: FIXED_DURATION,
      language: FIXED_LANGUAGE,
    });
    if (runNonceRef.current !== nonce) return;
    const { requestId, videoId } = gen;
    analytics.track(EVENTS.WAYMARK_TOPIC_HANDOFF, { source });
    setState((s) => ({ ...s, status: 'generating', videoId }));
    void persistState({
      status: 'generating',
      sourceUrl,
      topicId,
      generationRequestId: requestId,
      videoId,
    });
    await attachGenerationStatusSub(sdk, { requestId, videoId, sourceUrl, topicId, nonce });
  }, [persistState, attachGenerationStatusSub]);

  const attachTopicSearchStatusSub = React.useCallback(async (
    sdk: Waymark,
    args: { topicId: string; sourceUrl: string; nonce: number; probe?: boolean },
  ) => {
    const { topicId, sourceUrl, nonce, probe = false } = args;
    activeRunRef.current = { topicId, requestId: null, videoId: null, sourceUrl };
    await sdk.subscribeToTopicSearchStatusUpdates(topicId, async (update) => {
      if (runNonceRef.current !== nonce) return;
      if (update.status === 'FAILED') {
        const msg = update.error?.message ?? 'Topic search failed';
        analytics.track(EVENTS.WAYMARK_TOPIC_FAILED, { code: update.error?.code ?? null });
        setState((s) => ({ ...s, status: 'topic_failed', errorMessage: msg }));
        void persistState({
          status: 'topic_failed',
          sourceUrl,
          topicId,
          errorCode: update.error?.code ?? null,
          errorMessage: msg,
        });
        return;
      }
      if (update.status !== 'COMPLETE') return;
      if (topicHandoffRef.current === nonce) return;
      topicHandoffRef.current = nonce;
      try {
        await beginVideoGeneration(sdk, { topicId, sourceUrl, nonce, source: 'event' });
      } catch (e: unknown) {
        if (runNonceRef.current !== nonce) return;
        const msg = e instanceof Error ? e.message : 'Video generation failed to start';
        setState((s) => ({ ...s, status: 'generation_failed', errorMessage: msg }));
        void persistState({
          status: 'generation_failed',
          sourceUrl,
          topicId,
          errorMessage: msg,
        });
      }
    });

    if (!probe) return;
    // CTV-800 resume probe (see TOPIC_PROBE_*). Only for a re-attached read:
    // a fresh run's subscription is live from the first poll.
    const startedAt = Date.now();
    const tick = async () => {
      topicProbeTimerRef.current = null;
      if (runNonceRef.current !== nonce) return;
      if (stateRef.current.status !== 'topic_searching') return;
      if (topicHandoffRef.current === nonce) return;
      if (Date.now() - startedAt > TOPIC_PROBE_MAX_MS) {
        const msg = 'Could not pick this up after resuming. Try again.';
        setState((s) => ({ ...s, status: 'topic_failed', errorMessage: msg }));
        void persistState({ status: 'topic_failed', sourceUrl, topicId, errorMessage: msg });
        return;
      }
      topicHandoffRef.current = nonce;
      try {
        await beginVideoGeneration(sdk, { topicId, sourceUrl, nonce, source: 'probe' });
        console.info('[waymark.resume] website read recovered by probe', { topicId });
      } catch (e) {
        if (runNonceRef.current !== nonce) return;
        // Not complete yet (or Waymark said no): let the subscription have
        // it, and ask again later.
        topicHandoffRef.current = null;
        console.info('[waymark.resume] read not ready yet, will ask again', {
          topicId,
          message: e instanceof Error ? e.message : String(e),
        });
        topicProbeTimerRef.current = window.setTimeout(() => void tick(), TOPIC_PROBE_INTERVAL_MS);
      }
    };
    topicProbeTimerRef.current = window.setTimeout(() => void tick(), TOPIC_PROBE_DELAY_MS);
  }, [persistState, beginVideoGeneration]);

  const startGenerationForUrl = React.useCallback((url: string) => {
    const trimmed = normalizeUrl(url);
    if (!trimmed) return;
    // No-op if we're already running against this URL and not in a failure
    // state. Lets the wizard call this on every Next click without worrying
    // about wasted Waymark credits.
    if (
      inflightUrlRef.current === trimmed &&
      (state.status === 'topic_searching' ||
        state.status === 'generating' ||
        state.status === 'ready')
    ) {
      return;
    }

    inflightUrlRef.current = trimmed;
    const myNonce = ++runNonceRef.current;
    generationStartedAtRef.current = Date.now();
    // CTV-775 ledger: a fresh run starts a fresh receipt. The kickoff is the
    // 'analyzing' entry (stamped here rather than via the stage effect, which
    // sees no change when restarting while already analyzing).
    setStageEnteredAt({ analyzing: Date.now() });
    analytics.track(EVENTS.WAYMARK_GENERATION_STARTED);

    setState({
      status: 'topic_searching',
      sourceUrl: trimmed,
      videoId: null,
      renderedUrl: null,
      errorMessage: null,
      // A fresh run clears any prior partner-unavailable flag; if the new token
      // request fails the same way, requestAuth re-sets it.
      partnerUnavailable: false,
    });
    // Clear the prior IDs server-side too so a webhook for the old run
    // can't promote us out of the new one. `restart` tells the server this
    // reset is deliberate (it would otherwise refuse to downgrade a row the
    // webhook already promoted to ready + rendered).
    void persistState({
      status: 'topic_searching',
      sourceUrl: trimmed,
      topicId: null,
      generationRequestId: null,
      videoId: null,
      renderedUrl: null,
      errorCode: null,
      errorMessage: null,
      restart: true,
    });

    (async () => {
      const sdk = await ensureSDK();
      if (!sdk || runNonceRef.current !== myNonce) return;

      let topicId: string;
      try {
        const result = await sdk.startTopicSearch(trimmed);
        topicId = result.topicId;
      } catch (e: unknown) {
        if (runNonceRef.current !== myNonce) return;
        const msg = e instanceof Error ? e.message : 'Topic search failed to start';
        setState((s) => ({ ...s, status: 'topic_failed', errorMessage: msg }));
        void persistState({ status: 'topic_failed', sourceUrl: trimmed, errorMessage: msg });
        return;
      }

      void persistState({ status: 'topic_searching', sourceUrl: trimmed, topicId });

      await attachTopicSearchStatusSub(sdk, { topicId, sourceUrl: trimmed, nonce: myNonce });
    })();
  }, [ensureSDK, persistState, state.status, attachTopicSearchStatusSub]);

  // Resume in-flight subscriptions after page refresh. If state was persisted
  // as `topic_searching` or `generating`, the SDK from the previous session is
  // long gone — we need to re-attach to the existing topicId / requestId so
  // polling continues, otherwise the wizard sits forever on "generating" with
  // no way to learn the run finished. One-shot via ref to avoid re-resuming
  // on every status flip.
  React.useEffect(() => {
    if (hasResumedRef.current) return;
    if (!hasHydratedRef.current) return;
    if (!initial) return;
    if (!canReachServer) return;
    const sourceUrl = initial.sourceUrl;
    if (!sourceUrl) return;
    if (initial.status === 'topic_searching' && !initial.topicId) {
      // CTV-775: an orphaned run. topic_searching was persisted but the tab
      // died (or navigated) before startTopicSearch returned a topicId, so
      // there is nothing to re-attach to and the banner would sit on
      // "Analyzing" forever. Nothing exists on Waymark's side yet, so simply
      // restart the run. inflightUrlRef must be cleared first or the
      // same-URL no-op in startGenerationForUrl swallows the restart.
      hasResumedRef.current = true;
      console.info('[waymark.resume] orphaned topic_searching, restarting', { sourceUrl });
      inflightUrlRef.current = null;
      startGenerationForUrl(sourceUrl);
    } else if (initial.status === 'topic_searching' && initial.topicId) {
      hasResumedRef.current = true;
      const nonce = ++runNonceRef.current;
      inflightUrlRef.current = sourceUrl;
      console.info('[waymark.resume] topic_searching', { topicId: initial.topicId, sourceUrl });
      void (async () => {
        const sdk = await ensureSDK();
        if (!sdk || runNonceRef.current !== nonce) return;
        await attachTopicSearchStatusSub(sdk, {
          topicId: initial.topicId!,
          sourceUrl,
          nonce,
          probe: true,
        });
      })();
    } else if (
      initial.status === 'generating' &&
      initial.generationRequestId &&
      initial.videoId &&
      initial.topicId
    ) {
      hasResumedRef.current = true;
      const nonce = ++runNonceRef.current;
      inflightUrlRef.current = sourceUrl;
      console.info('[waymark.resume] generating', {
        requestId: initial.generationRequestId,
        videoId: initial.videoId,
        sourceUrl,
      });
      void (async () => {
        const sdk = await ensureSDK();
        if (!sdk || runNonceRef.current !== nonce) return;
        // Belt-and-suspenders: probe getVideoData before re-subscribing.
        // If the video already has a successful broadcast_quality render
        // (e.g. user Finished in a prior session and webhook didn't promote
        // server state, or Waymark's subscribe doesn't re-emit terminal
        // status for old requests), short-circuit to 'ready' instead of
        // waiting indefinitely on a subscription that won't fire.
        try {
          const videoData = await sdk.getVideoData(initial.videoId!);
          if (runNonceRef.current !== nonce) return;
          const rendered = pickBroadcastRender(videoData?.renders ?? []);
          if (rendered?.url) {
            console.info('[waymark.resume] short-circuit: video already rendered', {
              videoId: initial.videoId,
              url: rendered.url,
            });
            setState((s) => ({
              ...s,
              status: 'ready',
              videoId: initial.videoId!,
              renderedUrl: rendered.url,
              errorMessage: null,
            }));
            void persistState({
              status: 'ready',
              sourceUrl,
              videoId: initial.videoId!,
              renderedUrl: rendered.url,
            });
            return;
          }
        } catch (e) {
          console.warn('[waymark.resume] getVideoData probe failed', e);
        }
        if (runNonceRef.current !== nonce) return;
        await attachGenerationStatusSub(sdk, {
          requestId: initial.generationRequestId!,
          videoId: initial.videoId!,
          sourceUrl,
          topicId: initial.topicId!,
          nonce,
        });
      })();
    } else if (initial.status === 'ready' && initial.videoId && !initial.renderedUrl) {
      // User clicked Finish in the editor (which persisted status='ready'
      // with renderedUrl=null) and then left the wizard entirely before
      // `videoRendered` fired. The SDK subscription died with the unmount,
      // so we never picked up the broadcast-quality URL. Probe getVideoData
      // on remount — if the render is sitting on Waymark's side, populate
      // renderedUrl so the auto-import picks it up.
      hasResumedRef.current = true;
      const nonce = ++runNonceRef.current;
      inflightUrlRef.current = sourceUrl;
      console.info('[waymark.resume] ready, probing for missing renderedUrl', {
        videoId: initial.videoId,
      });
      void (async () => {
        const sdk = await ensureSDK();
        if (!sdk || runNonceRef.current !== nonce) return;
        try {
          const videoData = await sdk.getVideoData(initial.videoId!);
          if (runNonceRef.current !== nonce) return;
          const rendered = pickBroadcastRender(videoData?.renders ?? []);
          if (rendered?.url) {
            console.info('[waymark.resume] ready: recovered renderedUrl', {
              videoId: initial.videoId,
              url: rendered.url,
            });
            setState((s) => ({ ...s, renderedUrl: rendered.url }));
            void persistState({
              status: 'ready',
              sourceUrl,
              videoId: initial.videoId!,
              renderedUrl: rendered.url,
            });
          }
        } catch (e) {
          console.warn('[waymark.resume] ready: getVideoData probe failed', e);
        }
      })();
    }
  }, [initial, canReachServer, ensureSDK, persistState, attachTopicSearchStatusSub, attachGenerationStatusSub, startGenerationForUrl]);

  // CTV-604: discard a dead (or suspect) SDK instance so ensureSDK builds a
  // fresh one. The node used to be yanked FIRST so a dead bridge could not
  // hang cleanup(); disposeSDK keeps that as the fallback but runs the
  // attached teardown (with its logout) first. See the CTV-800 cross-write
  // fix note on disposeSDK.
  const teardownSDK = React.useCallback(() => {
    const old = sdkRef.current;
    sdkRef.current = null;
    sdkInitPromiseRef.current = null;
    // Same commercial, same account: no logout needed, node out first.
    teardownPromiseRef.current = old ? disposeSDK(old, 'yank-first') : Promise.resolve();
  }, []);

  // Tear down + rebuild: fresh /waymark/token (mode=login once the account
  // exists), fresh iframe, fresh Waymark-side session. Re-attaches in-flight
  // generation subscriptions that died with the old iframe.
  const reinitSDK = React.useCallback(async (): Promise<Waymark | null> => {
    analytics.track(EVENTS.WAYMARK_SDK_REINIT);
    teardownSDK();
    const sdk = await ensureSDK();
    if (!sdk) return null;
    const status = stateRef.current.status;
    const run = activeRunRef.current;
    if (run) {
      const nonce = ++runNonceRef.current;
      if (status === 'topic_searching' && run.topicId) {
        await attachTopicSearchStatusSub(sdk, {
          topicId: run.topicId,
          sourceUrl: run.sourceUrl,
          nonce,
          probe: true,
        });
      } else if (status === 'generating' && run.requestId && run.videoId && run.topicId) {
        await attachGenerationStatusSub(sdk, {
          requestId: run.requestId,
          videoId: run.videoId,
          sourceUrl: run.sourceUrl,
          topicId: run.topicId,
          nonce,
        });
      }
    }
    return sdk;
  }, [teardownSDK, ensureSDK, attachTopicSearchStatusSub, attachGenerationStatusSub]);

  // Open the editor view on an SDK instance, with one reinit+retry on
  // failure. The SDK-side timeout means a dead bridge rejects instead of
  // hanging forever (the pre-CTV-604 behavior).
  const openEditorView = React.useCallback(async (
    sdk: Waymark,
    myNonce: number,
  ): Promise<OpenOutcome> => {
    const openOnce = (s: Waymark) => {
      const { videoId, sourceUrl } = stateRef.current;
      return videoId
        ? s.openEditorForVideo(videoId, { timeout: OPEN_EDITOR_TIMEOUT_MS })
        : s.openWaymark({ sourceUrl: sourceUrl ?? undefined, timeout: OPEN_EDITOR_TIMEOUT_MS });
    };
    try {
      await openOnce(sdk);
      return openNonceRef.current === myNonce ? 'opened' : 'stale';
    } catch (e) {
      if (openNonceRef.current !== myNonce) return 'stale';
      console.warn('[waymark] editor open failed — reinitializing and retrying', e);
      const fresh = await reinitSDK();
      if (openNonceRef.current !== myNonce) return 'stale';
      if (!fresh) return 'failed';
      try {
        await openOnce(fresh);
        return openNonceRef.current === myNonce ? 'opened' : 'stale';
      } catch (e2) {
        if (openNonceRef.current !== myNonce) return 'stale';
        console.warn('[waymark] editor open retry failed', e2);
        return 'failed';
      }
    }
  }, [reinitSDK]);

  const openEditor = React.useCallback(async () => {
    const myNonce = ++openNonceRef.current;
    autoRecoverCountRef.current = 0;
    analytics.track(EVENTS.WAYMARK_EDITOR_OPENED, {
      ms_since_generation_started: generationStartedAtRef.current
        ? Date.now() - generationStartedAtRef.current
        : null,
    });
    claimEditor(ownerIdRef.current);
    setEditorPhase('connecting');
    let sdk = await ensureSDK();
    if (openNonceRef.current !== myNonce) return;
    if (!sdk) {
      setEditorPhase('failed');
      return;
    }
    // Layer A liveness gate: the main framing-refusal repro is returning
    // after idle (Waymark's inner session expired) and opening the editor
    // onto a dead iframe. Probe first; rebuild silently if dead.
    const healthy = await probeHealth(sdk);
    if (openNonceRef.current !== myNonce) return;
    if (!healthy) {
      analytics.track(EVENTS.WAYMARK_BRIDGE_UNHEALTHY, { trigger: 'open_probe' });
      sdk = await reinitSDK();
      if (openNonceRef.current !== myNonce) return;
      if (!sdk) {
        setEditorPhase('failed');
        return;
      }
    }
    const outcome = await openEditorView(sdk, myNonce);
    if (outcome === 'stale') return;
    if (outcome === 'failed') {
      analytics.track(EVENTS.WAYMARK_EDITOR_RECOVERY_FAILED, { stage: 'open' });
      setEditorPhase('failed');
      return;
    }
    setEditorPhase('open');
  }, [ensureSDK, reinitSDK, openEditorView]);

  // Rebuild the SDK and reopen the editor view. Runs automatically when a
  // dead bridge is detected while the editor is open (the user just sees the
  // "Connecting…" spinner), and manually from the failed panel's "Reload
  // editor" button. The bridge is known-dead here, so no probe — straight to
  // reinit.
  const recoverEditor = React.useCallback(async () => {
    const myNonce = ++openNonceRef.current;
    claimEditor(ownerIdRef.current);
    setEditorPhase('connecting');
    const sdk = await reinitSDK();
    if (openNonceRef.current !== myNonce) return;
    if (!sdk) {
      analytics.track(EVENTS.WAYMARK_EDITOR_RECOVERY_FAILED, { stage: 'recover' });
      setEditorPhase('failed');
      return;
    }
    const outcome = await openEditorView(sdk, myNonce);
    if (outcome === 'stale') return;
    if (outcome === 'failed') {
      analytics.track(EVENTS.WAYMARK_EDITOR_RECOVERY_FAILED, { stage: 'recover' });
      setEditorPhase('failed');
      return;
    }
    analytics.track(EVENTS.WAYMARK_EDITOR_RECOVERED);
    setEditorPhase('open');
  }, [reinitSDK, openEditorView]);

  const closeEditor = React.useCallback(async () => {
    // CTV-568: flip the overlay state first, then fire-and-forget sdk.close().
    // sdk.close() drives the iframe SPA via postMessage; when the iframe is
    // showing a browser-native error page (e.g. Waymark's origin refused
    // framing on a session-timeout URL), the SPA is dead and that postMessage
    // never settles. Awaiting it would trap the user in the blank overlay.
    openNonceRef.current += 1;
    setEditorPhase('closed');
    const sdk = sdkRef.current;
    if (!sdk) return;
    void sdk.close().catch((e) => {
      console.warn('[waymark] close failed', e);
    });
  }, []);

  // Shared dead-bridge handler for the watchdog, refocus, and iframe-load
  // probes. While the editor is open, recover silently (the user only sees
  // the "Connecting…" spinner) up to MAX_AUTO_RECOVERIES per session; after
  // that, stop looping and show the failed panel with the manual button.
  const handleBridgeDown = React.useCallback((trigger: string) => {
    analytics.track(EVENTS.WAYMARK_BRIDGE_UNHEALTHY, { trigger });
    if (editorPhaseRef.current !== 'open') return;
    if (autoRecoverCountRef.current >= MAX_AUTO_RECOVERIES) {
      analytics.track(EVENTS.WAYMARK_EDITOR_RECOVERY_FAILED, { stage: 'auto' });
      setEditorPhase('failed');
      return;
    }
    autoRecoverCountRef.current += 1;
    void recoverEditor();
  }, [recoverEditor]);
  handleBridgeDownRef.current = handleBridgeDown;

  // CTV-800: focus guard for the CLOSED editor. Chromium does not apply
  // `inert` inside a cross-origin iframe (Chromium change 3302103, Feb 2022),
  // so the hidden SDK page can still pull keyboard focus when it loads or
  // re-auths, which is what left staff unable to type into the Studio's
  // inputs (they could only paste). When focus lands on the hidden iframe,
  // hand it straight back to whatever the user was in. Never runs while the
  // editor is open: focus belongs to the iframe then.
  React.useEffect(() => {
    if (isEditorOpen) return;
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (containerRef.current?.contains(target)) return;
      lastFocusedRef.current = target;
    };
    // The top window blurs when focus moves into a frame, and the parent
    // document's activeElement is then the <iframe> element itself.
    const onWindowBlur = () => {
      const iframe = containerRef.current?.querySelector('iframe');
      if (!iframe || document.activeElement !== iframe) return;
      analytics.track(EVENTS.WAYMARK_FOCUS_RECLAIMED);
      const prev = lastFocusedRef.current;
      if (prev && prev.isConnected) {
        prev.focus({ preventScroll: true });
      } else {
        (document.activeElement as HTMLElement | null)?.blur?.();
      }
    };
    document.addEventListener('focusin', onFocusIn);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [isEditorOpen]);

  // CTV-604 Layer B: while the editor is open, poll bridge health every 5s
  // and immediately when the tab regains visibility (the framing refusal is
  // usually triggered by session expiry while backgrounded).
  React.useEffect(() => {
    if (editorPhase !== 'open') return;
    let cancelled = false;
    const check = async (trigger: 'watchdog' | 'visibility') => {
      if (isProbingRef.current) return;
      const sdk = sdkRef.current;
      if (!sdk) return;
      isProbingRef.current = true;
      const ok = await probeHealth(sdk);
      isProbingRef.current = false;
      if (cancelled) return;
      if (!ok) handleBridgeDown(trigger);
    };
    const interval = setInterval(() => { void check('watchdog'); }, WATCHDOG_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check('visibility');
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [editorPhase, handleBridgeDown]);

  // CTV-801: the finalize wait's pull backstop.
  //
  // Between Finish (`videoCompleted`) and the broadcast render landing, the UI
  // shows "Uploading your commercial" and the ONLY thing that can end that
  // wait is a `videoRendered` event. That event is push-only: it fires once,
  // Waymark never redelivers it, and it can go missing several ways: the
  // socket drops, the tab is backgrounded, or the `email`-format emission
  // arrives and the `broadcast_quality` follow-up never does (which
  // pickBroadcastRender correctly declines, leaving us waiting on a second
  // emission that may not come). The CTV-604 watchdog above does not cover
  // this: it only runs while the editor is OPEN, and Finish closes it.
  //
  // So we also ask. getVideoData is idempotent and authoritative, and the
  // mount-time resume effect already proves the technique works. A manual
  // page refresh was, until now, the only retry mechanism in the system.
  //
  // The visibility probe is the highest-value trigger here, because a
  // backgrounded tab is the most common way the event goes missing, and it
  // stays armed past the interval cap so a returning buyer always gets a
  // fresh check.
  React.useEffect(() => {
    if (!isFinalizing) return;
    const videoId = state.videoId;
    if (!videoId) return;

    let cancelled = false;
    let timer: number | undefined;
    const startedAt = Date.now();
    // A late probe must never write into the commercial that replaced ours.
    const forCommercial = commercialIdRef.current;

    const probe = async (trigger: 'interval' | 'visibility') => {
      if (cancelled || isFinalizePollingRef.current) return;
      isFinalizePollingRef.current = true;
      try {
        const sdk = await ensureSDK();
        if (cancelled || !sdk || commercialIdRef.current !== forCommercial) return;
        const videoData = await withTimeout<VideoDataLike | null>(
          sdk.getVideoData(videoId),
          FINALIZE_PROBE_TIMEOUT_MS,
          null,
        );
        if (cancelled || commercialIdRef.current !== forCommercial) return;
        // Same options as the event path, and load-bearing: `finalizingRef`
        // carries the file we held before this Finish plus when it started, so
        // a re-edit of an already-rendered video cannot instantly re-adopt the
        // old render and call the new one done. (The mount-time probes pass no
        // options because at mount there is no prior file in hand.)
        const rendered = pickBroadcastRender(
          videoData?.renders ?? [],
          finalizingRef.current ?? {},
        );
        if (!rendered?.url) return;
        console.info('[waymark.finalize] render recovered by poll', {
          videoId,
          trigger,
          url: rendered.url,
        });
        adoptRender(videoId, rendered.url, 'poll');
      } catch (e) {
        // A failed probe is not a failed render. Stay in the wait and let the
        // next tick (or the event) settle it.
        console.warn('[waymark.finalize] poll probe failed', e);
      } finally {
        isFinalizePollingRef.current = false;
      }
    };

    // Self-rescheduling rather than setInterval so the cadence can widen as
    // the wait drags on, and so a slow probe can't stack ticks behind itself.
    const schedule = () => {
      const elapsed = Date.now() - startedAt;
      if (elapsed >= FINALIZE_POLL_MAX_MS) return;
      const delay =
        elapsed < FINALIZE_POLL_FAST_WINDOW_MS
          ? FINALIZE_POLL_FAST_INTERVAL_MS
          : FINALIZE_POLL_SLOW_INTERVAL_MS;
      timer = window.setTimeout(() => {
        void probe('interval').then(() => {
          if (!cancelled) schedule();
        });
      }, delay);
    };
    schedule();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void probe('visibility');
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isFinalizing, state.videoId, ensureSDK, adoptRender]);

  // Clean up the SDK on unmount — the provider lives for the whole wizard
  // session, so this only fires on logical teardown (user navigates away
  // from the slug entirely).
  React.useEffect(() => {
    return () => {
      const sdk = sdkRef.current;
      if (!sdk) return;
      sdkRef.current = null;
      void sdk.cleanup().catch(() => { /* noop */ });
    };
  }, []);

  // CTV-775 Start over: discard this commercial's current video and generate
  // again on the SAME row (same Waymark account, same URL). Clearing
  // inflightUrlRef defeats startGenerationForUrl's same-URL no-op; the fresh
  // run bumps the nonce (orphaning the old subscriptions), persists
  // topic_searching with null ids (so a late webhook for the old video can't
  // promote us), and rebuilds from scratch. Never creates a row: that is
  // Make another's job, at the surface level.
  const restartGeneration = React.useCallback(() => {
    const url = stateRef.current.sourceUrl ?? inflightUrlRef.current;
    if (!url) return;
    inflightUrlRef.current = null;
    setIsFinalizing(false);
    startGenerationForUrl(url);
  }, [startGenerationForUrl]);

  const value: WaymarkContextValue = {
    commercialId,
    status: state.status,
    sourceUrl: state.sourceUrl,
    videoId: state.videoId,
    renderedUrl: state.renderedUrl,
    errorMessage: state.errorMessage,
    isFinalizing,
    isHydrating,
    partnerUnavailable: state.partnerUnavailable,
    startGenerationForUrl,
    restartGeneration,
    openEditor,
    closeEditor,
    isEditorOpen,
    stageEnteredAt,
  };

  return (
    <WaymarkContext.Provider value={commercialId == null ? null : value}>
      {children}
      {/*
        The SDK iframe lives here for the whole wizard session — DOM-stable
        across step navigation so background topic/video generation polling
        survives. When closed, the container is a 1×1 transparent sliver. When
        open, it renders as a centered window (~90% viewport) over a dimmed
        backdrop, NOT a fullscreen takeover.

        The closed state must keep the iframe RENDERED (display:none /
        visibility:hidden can make the browser tear down and reload the
        iframe document, which would kill the bridge and any in-flight
        generation) while making it completely non-interactive:

          - `inert` keeps the subtree out of the focus order. Without it the
            SDK page steals focus the moment it loads or re-auths, which
            yanks the caret out of whatever wizard field the buyer is typing
            in (this fired on step 2, because the SDK boots on entry to
            step 3 once the creative step has created the AI commercial).
          - `position: fixed` keeps it off the scroll canvas. The container
            used to sit at left/top -99999px; anything inside it taking focus
            made the browser scroll that point into view, clamping the page to
            scroll 0 and snapping the buyer to the top mid-wizard.

        `inert` does not affect layout, loading, or postMessage, so the SDK
        bridge is unaffected.
      */}
      {isEditorOpen && (
        <div
          aria-hidden="true"
          onClick={() => { void closeEditor(); }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            zIndex: 999,
          }}
        />
      )}
      <div
        ref={containerRef}
        data-waymark-container
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- `inert` lands in React's JSX types in v19; this build is on 18.
        {...({ inert: isEditorOpen ? undefined : '' } as any)}
        style={
          isEditorOpen
            ? {
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'min(1100px, 92vw)',
                height: 'min(720px, 88vh)',
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: '0 25px 60px -15px rgba(15, 23, 42, 0.45)',
                background: '#fff',
                zIndex: 1000,
                pointerEvents: 'auto',
              }
            : {
                position: 'fixed',
                left: 0,
                top: 0,
                width: 1,
                height: 1,
                opacity: 0,
                zIndex: -1,
                pointerEvents: 'none',
              }
        }
      />
      {(editorPhase === 'connecting' || editorPhase === 'failed') && (
        <EditorOverlayPanel
          phase={editorPhase}
          onReload={() => {
            autoRecoverCountRef.current = 0;
            void recoverEditor();
          }}
          onClose={() => { void closeEditor(); }}
        />
      )}
    </WaymarkContext.Provider>
  );
};

// CTV-604 recovery panel. Opaque layer with the exact geometry of the editor
// container, stacked ABOVE the iframe — when the bridge dies it fully hides
// whatever the iframe is showing (typically the browser's own framing-refusal
// error page) and offers a way back into the editor.
const EditorOverlayPanel: React.FC<{
  phase: Extract<EditorPhase, 'connecting' | 'failed'>;
  onReload: () => void;
  onClose: () => void;
}> = ({ phase, onReload, onClose }) => (
  <div
    data-waymark-overlay-panel
    role={phase === 'connecting' ? 'status' : 'alertdialog'}
    aria-label={phase === 'connecting' ? 'Connecting to the video editor' : 'Video editor connection lost'}
    style={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 'min(1100px, 92vw)',
      height: 'min(720px, 88vh)',
      borderRadius: 12,
      overflow: 'hidden',
      background: '#fff',
      zIndex: 1001,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {phase === 'connecting' ? (
      <div className="flex flex-col items-center gap-3 text-slate-500">
        <span className="h-8 w-8 rounded-full border-2 border-slate-200 border-t-slate-500 animate-spin" aria-hidden />
        <p className="text-[13px] font-medium">Connecting to your editor…</p>
      </div>
    ) : (
      <div className="w-full max-w-md px-6">
        <div
          className="relative rounded-xl px-4 py-3"
          style={{
            background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
            border: '1px solid rgba(100, 116, 139, 0.18)',
          }}
        >
          <span
            aria-hidden
            className="absolute left-0 top-3 bottom-3 w-[2px] rounded-r"
            style={{ background: '#64748b' }}
          />
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 pl-2">
            Connection interrupted
          </p>
          <p className="text-[12.5px] text-slate-700 leading-relaxed pl-2 mt-1">
            We couldn&apos;t reconnect to the video editor. Your work is saved
            automatically. Reload to pick up where you left off.
          </p>
        </div>
        <div className="flex items-center justify-end gap-4 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="text-[12px] font-medium text-slate-500 hover:text-slate-700 transition-colors"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onReload}
            className="inline-flex items-center justify-center px-4 py-1.5 rounded-full text-[12px] font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98]"
            style={{
              background: 'linear-gradient(180deg, #3b82f6, #1d4ed8)',
              boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 14px -8px #1d4ed8',
            }}
          >
            Reload editor
          </button>
        </div>
      </div>
    )}
  </div>
);
